import { useEffect, useMemo, useState } from 'react'
import {
    BoldItalicUnderlineToggles,
    CreateLink,
    diffSourcePlugin,
    headingsPlugin,
    linkDialogPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../api'
import { ButtonStatus, FileType } from '../common/constants'
import Back from '../components/Back'
import Button from '../components/Button'
import Card from '../components/cards/Card'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import Select from '../components/Select'
import Table from '../components/Table'
import TextInput from '../components/TextInput'
import Uploader from '../components/Uploader'
import typo from '../typography.module.css'
import styles from './VideoEditor.module.css'

const slugify = (value = '') => {
    return value
        .replace(/\.[^/.]+$/, '')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()
        .trim()
}

const getFileInfoFromUrl = (url) => {
    try {
        const pathname = new URL(url).pathname
        const fileName = decodeURIComponent(pathname.split('/').pop() || '')
        const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : null
        const baseName = fileName.replace(/\.[^/.]+$/, '')
        return { fileName, ext, baseName }
    } catch (_) {
        return { fileName: '', ext: null, baseName: '' }
    }
}

const DownloadEditor = () => {
    const { download_id } = useParams()
    const navigate = useNavigate()

    const [download, setDownload] = useState(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [slug, setSlug] = useState('')
    const [status, setStatus] = useState('draft')

    const [fileUrl, setFileUrl] = useState('')
    const [fileSize, setFileSize] = useState(null)
    const [fileType, setFileType] = useState(null)
    const [uploaderReset, setUploaderReset] = useState(null)
    const [downloaders, setDownloaders] = useState([])
    const [downloadersLoading, setDownloadersLoading] = useState(false)

    const [publishing, setPublishing] = useState(null)

    const statusOptions = useMemo(() => ([
        { id: 1, name: 'Bozza', value: 'draft' },
        { id: 2, name: 'Pubblico', value: 'public' },
        { id: 3, name: 'Nascosto', value: 'hidden' },
    ]), [])

    useEffect(() => {
        const getDownload = async () => {
            try {
                const data = await api.get(`/admin/downloads/${download_id}`)
                setDownload(data)
            } catch (e) {
                console.error(e)
            }
        }

        if (download_id !== 'create') {
            getDownload()
        }
    }, [download_id])

    useEffect(() => {
        const getDownloaders = async () => {
            if (download_id === 'create') {
                setDownloaders([])
                return
            }

            setDownloadersLoading(true)
            try {
                const data = await api.get(`/admin/downloads/${download_id}/downloaders`)
                setDownloaders(data.downloaders || [])
            } catch (e) {
                console.error(e)
                setDownloaders([])
            }
            setDownloadersLoading(false)
        }

        getDownloaders()
    }, [download_id])

    useEffect(() => {
        if (!download) {
            return
        }

        setName(download.name || '')
        setDescription(download.description || '')
        setSlug(download.slug || '')
        setStatus(download.status || 'draft')

        setFileUrl(download.file_url || '')
        setFileSize(download.file_size || null)
        setFileType(download.file_type || null)
    }, [download])

    const saveDownload = async () => {
        try {
            setPublishing(ButtonStatus.Loading)
            const payload = {
                name,
                slug,
                description,
                file_url: fileUrl,
                file_size: fileSize,
                file_type: fileType,
                status,
            }

            const response = download_id === 'create'
                ? await api.post('/admin/downloads', payload)
                : await api.put(`/admin/downloads/${download_id}`, payload)

            setPublishing(ButtonStatus.Success)
            setUploaderReset((prev) => !prev)

            if (download_id === 'create') {
                navigate(`/downloads/${response.id}/editor`)
            }
        } catch (e) {
            setPublishing(ButtonStatus.Error)
            console.error(e)
        }
    }

    const exportDownloadersCsv = async () => {
        if (download_id === 'create') {
            return
        }

        try {
            const blob = await api.get(`/admin/downloads/${download_id}/downloaders/csv`, {
                responseType: 'blob',
            })

            const fileUrl = window.URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            const safeSlug = slug || `download_${download_id}`
            anchor.href = fileUrl
            anchor.download = `${safeSlug}_downloaders.csv`
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            window.URL.revokeObjectURL(fileUrl)
        } catch (e) {
            console.error(e)
        }
    }

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Editor download</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <Back onClick={() => navigate(-1)} />
                        <div className={styles.header}>
                            <div className={typo.title} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                {download_id === 'create' ? 'Aggiungi download' : 'Modifica download'}
                            </div>
                        </div>
                        <Card>
                            <div className={styles.content}>
                                <div className={styles.top}>
                                    <div className={styles.left}>
                                        <div className={styles.labeled}>
                                            <div className={typo.caption}>NOME</div>
                                            <TextInput
                                                type="text"
                                                value={name}
                                                placeholder="Nome"
                                                onKeyUp={(value) => {
                                                    setName(value)
                                                    if (download_id === 'create' && !slug) {
                                                        setSlug(slugify(value))
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className={styles.labeled}>
                                            <div className={typo.caption}>SLUG</div>
                                            <TextInput
                                                type="text"
                                                value={slug}
                                                placeholder="Slug"
                                                onKeyUp={(value) => setSlug(slugify(value))}
                                            />
                                        </div>
                                        <div className={styles.labeled}>
                                            <div className={typo.caption}>STATO</div>
                                            <Select
                                                selected={statusOptions.find((s) => s.value === status)}
                                                options={statusOptions}
                                                onSelect={(value) => setStatus(value.value)}
                                            />
                                        </div>
                                        <div className={styles.labeled}>
                                            <div className={typo.caption}>DESCRIZIONE</div>
                                            <div className={styles.editor}>
                                                <MDXEditor
                                                    contentEditableClassName={styles.markdownEditor}
                                                    markdown={description || ''}
                                                    placeholder="Descrizione"
                                                    onChange={(value) => setDescription(value)}
                                                    plugins={[
                                                        headingsPlugin(),
                                                        listsPlugin(),
                                                        quotePlugin(),
                                                        thematicBreakPlugin(),
                                                        linkDialogPlugin(),
                                                        diffSourcePlugin(),
                                                        tablePlugin(),
                                                        toolbarPlugin({
                                                            toolbarContents: () => (
                                                                <>
                                                                    <BoldItalicUnderlineToggles options={["Bold", "Italic"]} />
                                                                    <ListsToggle />
                                                                    <CreateLink />
                                                                </>
                                                            )
                                                        })
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.labeled}>
                                            <div className={typo.caption}>URL FILE</div>
                                            <TextInput
                                                type="text"
                                                value={fileUrl}
                                                placeholder="Url file"
                                                onKeyUp={(value) => setFileUrl(value)}
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.right}>
                                        <div className={styles.switch}>
                                            <div className={styles.tab}>File</div>
                                        </div>
                                        <div className={styles.mediaContainer}>
                                            <Uploader
                                                preview={fileUrl}
                                                reset={uploaderReset}
                                                multiple={false}
                                                type={FileType.All}
                                                onComplete={(uploads) => {
                                                    const uploaded = uploads?.[0]
                                                    if (!uploaded?.url) {
                                                        return
                                                    }

                                                    const fromFileName = uploaded.file?.name || ''
                                                    const parsed = fromFileName
                                                        ? {
                                                            fileName: fromFileName,
                                                            ext: fromFileName.includes('.') ? fromFileName.split('.').pop().toLowerCase() : null,
                                                            baseName: fromFileName.replace(/\.[^/.]+$/, ''),
                                                        }
                                                        : getFileInfoFromUrl(uploaded.url)

                                                    setFileUrl(uploaded.url)
                                                    setFileSize(uploaded.file?.size || null)
                                                    setFileType(parsed.ext || null)

                                                    if (download_id === 'create') {
                                                        if (!name) {
                                                            setName(parsed.baseName || 'download')
                                                        }
                                                        setSlug((prev) => prev || slugify(parsed.baseName || parsed.fileName || 'download'))
                                                    }
                                                }}
                                                onDelete={() => {
                                                    setFileUrl('')
                                                    setFileSize(null)
                                                    setFileType(null)
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.action}>
                                    <Button
                                        status={publishing}
                                        disabled={!name || !slug || !fileUrl}
                                        style={{ padding: '0.75rem 2rem' }}
                                        onClick={saveDownload}
                                    >
                                        {download_id === 'create' ? 'PUBBLICA' : 'SALVA'}
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        {download_id !== 'create' && (
                            <div style={{ marginTop: '1rem' }}>
                                <div className={styles.header} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <div className={typo.subtitle}>Utenti</div>
                                    <Button appearance="subtle" onClick={exportDownloadersCsv}>
                                        Esporta CSV
                                    </Button>
                                </div>
                                <Table
                                    appearance="minimal"
                                    loading={downloadersLoading}
                                    loadingN={5}
                                    style={{ backgroundColor: 'var(--background-color)' }}
                                    columns={[
                                        { key: 'user_id', label: 'USER ID', type: 'number' },
                                        { key: 'email', label: 'EMAIL', type: 'string' },
                                        { key: 'name', label: 'NOME', type: 'string' },
                                        { key: 'surname', label: 'COGNOME', type: 'string' },
                                        { key: 'phone', label: 'TELEFONO', type: 'string' },
                                        { key: 'city', label: 'CITTA', type: 'string' },
                                        { key: 'downloads_count', label: 'DOWNLOAD', type: 'number' },
                                        { key: 'downloaded_at', label: 'PRIMO DOWNLOAD', type: 'datetime' },
                                        { key: 'last_download_at', label: 'ULTIMO DOWNLOAD', type: 'datetime' },
                                    ]}
                                    elements={downloaders}
                                    defaultSort={{ key: 'last_download_at', direction: 'desc' }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </HeaderFooterLayout>
    )
}

export default DownloadEditor
